import { SquareLock02Icon, Tick02Icon } from '@hugeicons/react'
import clsx from 'clsx'

import type { LevelUiStatus } from '../constants/status'

export type TimelineConnectorTone = 'success' | 'progress' | 'available' | 'locked'

export type TimelineDotSize = 'phase' | 'level'

export function getConnectorToneFromStatus(
  status: LevelUiStatus
): TimelineConnectorTone {
  if (status === 'success') return 'success'
  if (status === 'progress') return 'progress'
  if (status === 'available') return 'available'
  return 'locked'
}

const CONNECTOR_TONE_CLASS: Record<TimelineConnectorTone, string> = {
  success: 'bg-status-success',
  progress: 'bg-status-progress',
  available: 'bg-brand',
  locked: 'bg-[#d1d5db]'
}

interface TimelineRowProps {
  dot: React.ReactNode
  dotSize?: TimelineDotSize
  showConnector?: boolean
  showConnectorPre?: boolean
  /** Estende o conector-pré 20px acima da linha — ponte pro gap `mb-5` de uma
   *  fase renderizada fora da TimelineRow (fase única), cujo conector principal
   *  não existe pra atravessar a margem. */
  connectorPreBridge?: boolean
  connectorTone?: TimelineConnectorTone
  connectorPreTone?: TimelineConnectorTone
  connectorPreGradient?: boolean
  children: React.ReactNode
  className?: string
}

export function TimelineRow({
  dot,
  dotSize = 'level',
  showConnector = true,
  showConnectorPre = false,
  connectorPreBridge = false,
  connectorTone = 'locked',
  connectorPreTone = 'locked',
  connectorPreGradient = false,
  children,
  className
}: TimelineRowProps) {
  const isPhase = dotSize === 'phase'

  return (
    <div className={clsx('flex gap-3', isPhase ? 'mb-5' : 'mb-3', className)}>
      {/* Marcador e conectores centralizam em 50% da altura real do card (via
          `self-stretch`), não num offset fixo em px — card de nível varia de
          altura (hint de bloqueio quebra em 2-3 linhas) e um offset fixo
          desalinhava o marcador do centro visual do card. */}
      <div className="relative flex-[0_0_36px] self-stretch">
        {showConnectorPre && (
          <div
            className={clsx(
              'absolute left-1/2 w-[3px] -translate-x-1/2 z-[1] rounded-sm bottom-1/2',
              connectorPreBridge ? '-top-5' : 'top-0',
              connectorPreGradient
                ? 'bg-gradient-to-b from-status-success to-status-progress'
                : CONNECTOR_TONE_CLASS[connectorPreTone]
            )}
            aria-hidden
          />
        )}
        <div className="absolute left-1/2 top-1/2 z-[2] -translate-x-1/2 -translate-y-1/2 flex items-center justify-center">
          {dot}
        </div>
        {/* O espaçamento entre linhas é margin (fora da altura de referência do
            marcador) — o conector precisa atravessá-la com bottom negativo do
            mesmo tamanho, senão a trilha abre um buraco a cada gap. */}
        {showConnector && (
          <div
            className={clsx(
              'absolute left-1/2 top-1/2 w-[3px] -translate-x-1/2 z-[1] rounded-sm',
              isPhase ? '-bottom-5' : '-bottom-3',
              CONNECTOR_TONE_CLASS[connectorTone]
            )}
            aria-hidden
          />
        )}
      </div>
      <div className="flex-1 min-w-0">{children}</div>
    </div>
  )
}

const PHASE_CIRCLE_CLASS: Record<LevelUiStatus, string> = {
  success: 'bg-status-success text-white shadow-[0_2px_8px_rgba(22,163,74,0.35)]',
  progress: 'bg-status-progress text-white shadow-[0_2px_8px_rgba(255,152,0,0.4)]',
  available: 'bg-white text-brand border-[2.5px] border-brand shadow-[0_0_0_2px_color-mix(in_oklab,var(--color-brand)_10%,transparent)]',
  locked: 'bg-gray-500 text-white'
}

interface PhaseTimelineDotProps {
  uiStatus: LevelUiStatus
  stageIndex: number
  isOpen: boolean
  onToggle: () => void
}

export function PhaseTimelineDot({
  uiStatus,
  stageIndex,
  isOpen,
  onToggle
}: PhaseTimelineDotProps) {
  return (
    <div
      className={clsx(
        'w-9 h-9 rounded-full flex items-center justify-center text-sm font-extrabold shrink-0 select-none transition-transform duration-[120ms] ease-in active:scale-[0.92] cursor-pointer hover:brightness-95',
        PHASE_CIRCLE_CLASS[uiStatus]
      )}
      onClick={onToggle}
      role="button"
      aria-label={isOpen ? `Fechar fase ${stageIndex}` : `Abrir fase ${stageIndex}`}
      aria-expanded={isOpen}
    >
      {uiStatus === 'success' ? (
        <Tick02Icon size={16} color="#fff" strokeWidth={3} />
      ) : uiStatus === 'locked' ? (
        <SquareLock02Icon size={14} color="#fff" />
      ) : (
        stageIndex
      )}
    </div>
  )
}

const DOT_RING_BORDER_CLASS: Record<TimelineConnectorTone, string> = {
  success: 'border-status-success',
  progress: 'border-status-progress',
  available: 'border-brand',
  locked: 'border-gray-600'
}

interface LevelTimelineDotProps {
  status: LevelUiStatus
  /** Tom agregado da fase (`progress`) — sobrepõe o preenchimento independente
   *  por status pra reduzir poluição visual numa fase parcialmente concluída.
   *  Nível bloqueado nunca recebe o tom da fase, mesmo com a fase em progresso. */
  phaseTone?: TimelineConnectorTone
}

export function LevelTimelineDot({ status, phaseTone }: LevelTimelineDotProps) {
  if (phaseTone && status !== 'locked') {
    return (
      <div
        className={clsx(
          'w-5 h-5 rounded-full bg-white flex items-center justify-center shrink-0 border-[2.5px]',
          DOT_RING_BORDER_CLASS[phaseTone]
        )}
      >
        {status === 'success' && (
          <Tick02Icon size={11} color="var(--color-status-success)" strokeWidth={3} />
        )}
      </div>
    )
  }

  if (status === 'success') {
    return (
      <div className="w-5 h-5 rounded-full bg-status-success flex items-center justify-center shrink-0 shadow-[0_1px_4px_rgba(22,163,74,0.35)]">
        <Tick02Icon size={11} color="#fff" strokeWidth={3} />
      </div>
    )
  }

  if (status === 'progress') {
    return (
      <div
        className="w-4 h-4 rounded-full border-[2.5px] border-status-progress bg-white shrink-0 shadow-[0_0_0_2px_rgba(255,152,0,0.15)]"
        aria-hidden
      />
    )
  }

  if (status === 'available') {
    return (
      <div
        className="w-[14px] h-[14px] rounded-full border-[2.5px] border-brand bg-white shrink-0 shadow-[0_0_0_2px_color-mix(in_oklab,var(--color-brand)_12%,transparent)]"
        aria-hidden
      />
    )
  }

  return (
    <div className="w-5 h-5 rounded-full bg-gray-700 flex items-center justify-center shrink-0">
      <SquareLock02Icon size={12} color="#fff" strokeWidth={2} />
    </div>
  )
}
